import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { saveAs } from 'file-saver';
import { from } from 'rxjs';
import { File as FileModel, SiteVendorFile } from 'src/app/classes/models';
import { GenericObject } from 'src/app/interfaces/generic-object';
import { GridActionColumnActions } from 'src/app/interfaces/grid-action-column-actions';
import { GridActionColumnMap } from 'src/app/interfaces/grid-action-column-map';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { FileUploadModalComponent } from 'src/app/modals/file-upload-modal/file-upload-modal.component';
import { SiteService } from 'src/app/services/site.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';

@Component({
  selector: 'app-site-vendor-files',
  standalone: false,
  templateUrl: './site-vendor-files.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './site-vendor-files.component.less'
})
export class SiteVendorFilesComponent implements OnInit {
  constructor(
    private route: ActivatedRoute,
    private ModalService: NgbModal,
    private ToastService: ToastService,
    private SiteService: SiteService,
    private UtilityService: UtilityService
  ) {}

  SiteVendorId?: number;

  ColumnIdToColumnActions: GridActionColumnMap = {
    ['$actionColumn']: ['delete', 'download']
  };

  SuppliedQueryData?: GenericObject;

  reloadSiteVendorFileGridData: boolean = false;

  uploadFile() {
    const { componentInstance, result } = this.ModalService.open(
      FileUploadModalComponent,
      { backdrop: 'static', size: 'lg' }
    );

    Object.assign(componentInstance, {
      helperText: 'A maximum of 20MB of files can be uploaded at once.'
    });

    from(result).subscribe({
      next: (Files) => {
        const SiteVendorFiles = Files.map(
          (File: FileModel) =>
            new SiteVendorFile({ SiteVendorId: this.SiteVendorId, File })
        );

        this.SiteService.saveSiteVendorFiles(SiteVendorFiles).subscribe({
          next: () => {
            this.ToastService.addSuccessToast(
              'Success',
              'Site vendor files have been added.'
            );

            this.reloadSiteVendorFileGridData = true;
          }
        });
      }
    });
  }

  action({
    action,
    rowData
  }: {
    action: GridActionColumnActions;
    rowData: SiteVendorFile;
  }) {
    const { id: SiteVendorFileId, SiteVendorId, FileId } = rowData;
    if (action === 'download') {
      this.SiteService.getSiteVendorFile(
        this.UtilityService.stringifyObjectsInQueryObject({
          attributes: ['id'],
          id: SiteVendorFileId,
          SiteVendor: {
            options: { required: true },
            attributes: ['id']
          },
          File: {
            options: { required: true }
          }
        })
      ).subscribe({
        next: (SiteVendorFile) => {
          if (!SiteVendorFile) {
            throw this.ToastService.addErrorToast(
              'Error',
              'There was an error downloading the file.',
              { returnError: true }
            );
          }

          const { File, SiteVendor } = SiteVendorFile;

          if (!File) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Missing file information.',
              { returnError: true }
            );
          }

          if (!SiteVendor) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Missing site vendor information.',
              { returnError: true }
            );
          }

          const { id: SiteVendorId } = SiteVendor;

          if (!SiteVendorId) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Missing SiteVendorId.',
              { returnError: true }
            );
          }

          const { id: FileId, filepath } = File;

          if (!FileId) {
            throw this.ToastService.addErrorToast('Error', 'Missing file id.', {
              returnError: true
            });
          }

          if (!filepath) {
            throw this.ToastService.addErrorToast(
              'Error',
              'No file path found.',
              { returnError: true }
            );
          }

          this.SiteService.getSiteVendorFileS3PresignedUrl(
            SiteVendorId,
            FileId
          ).subscribe({
            next: ({ presignedUrl, filename }) => {
              saveAs(presignedUrl, filename);
            }
          });
        }
      });
    }

    if (action === 'delete') {
      const { componentInstance, result } = this.ModalService.open(
        ConfirmModalComponent,
        { backdrop: 'static', size: 'md' }
      );

      Object.assign(componentInstance, {
        displayText: 'Are you sure you want to delete this file?'
      });

      from(result).subscribe({
        next: (confirm) => {
          if (confirm) {
            this.SiteService.deleteSiteVendorFile({
              id: SiteVendorFileId,
              SiteVendorId,
              FileId
            }).subscribe({
              next: (count) => {
                if (count === 0) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'There was an error deleting the file.',
                    { returnError: true }
                  );
                }
                this.ToastService.addSuccessToast(
                  'Success',
                  'Successfully deleted file.'
                );
                this.reloadSiteVendorFileGridData = true;
              }
            });
          }
        }
      });
    }
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe({
      next: (ParamMap) => {
        const SiteVendorId = Number(ParamMap.get('SiteVendorId'));

        if (!SiteVendorId) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing site vendor id.',
            { returnError: true }
          );
        }

        this.SuppliedQueryData = {
          attributes: ['SiteVendorId', 'FileId'],
          SiteVendorId: SiteVendorId
        };

        this.SiteVendorId = SiteVendorId;
      }
    });
  }
}
